import type { App } from 'vue'
import { createI18n } from 'vue-i18n'

import { detectInitialLocale, isDevEnv, setLocale } from './locale'
import { DEFAULT_LOCALE } from './locales'
import commonEn from './messages/common.en.json'
import errorsEn from './messages/errors.en.json'
import uiEn from './messages/ui.en.json'
import validationEn from './messages/validation.en.json'

// Module 04 — the vue-i18n runtime (contract 04 §8). Composition-API mode (`legacy:false`);
// number/date formatting is DELIBERATELY not configured here — `numberFormats`/`datetimeFormats`
// are omitted so values funnel through `useFormat()` (one Intl cache), keeping `t()` for text
// only (contract §9).
//
// The four reserved namespace bundles (`common`/`ui`/`errors`/`validation`, ORP-18597) are
// imported directly here as the concrete `en` base seed — a typed object (not the loader's
// `Record<string, unknown>` glob result) so vue-i18n keeps its global message typing. This makes
// first-paint copy (error toasts, buttons, sr-only labels, `common.appName`) render before
// `setupI18n` runs. At boot `setLocale` → `loadLocaleMessages('en')` re-merges the same files via
// the loader's eager glob, so adding a locale or a key needs no edit here.

/** The single vue-i18n instance. `app/bootstrap` installs it via `setupI18n`; the barrel
 *  re-exports it so out-of-component callers (e.g. the http `onError` toast) can resolve keys.
 *  The warn flags are re-synced in `setupI18n` once `validateEnv()` has run (this instance is
 *  created at import, before boot). */
export const i18n = createI18n({
  legacy: false,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  // Missing-key + fallback noise is a development aid only — silent in production.
  missingWarn: isDevEnv(),
  fallbackWarn: isDevEnv(),
  messages: {
    [DEFAULT_LOCALE]: { common: commonEn, ui: uiEn, errors: errorsEn, validation: validationEn },
  },
})

/** Boot installer (contract 04 §8, `app/bootstrap` i18n step). Runs the locale-detection
 *  fallback chain, awaits the resolved locale's bundle so the first paint has copy in the
 *  right language, then registers the plugin. Ordering (owned by bootstrap): after Pinia
 *  (needs `localeStore`), before the router mounts (guards/screens use `t`). */
export async function setupI18n(app: App): Promise<void> {
  // Re-sync the dev-only warn policy now that env is validated (the instance above was created
  // at import, before bootstrap called validateEnv()).
  i18n.global.missingWarn = isDevEnv()
  i18n.global.fallbackWarn = isDevEnv()

  const initial = detectInitialLocale()
  // `setLocale` merges the reserved bundle, stamps `<html lang>`, and syncs `localeStore`.
  // For `en` this is cheap (reserved already seeded); it never rejects for a supported locale
  // unless the chunk fails, in which case we degrade to the seeded default rather than block boot.
  try {
    await setLocale(initial)
  } catch (cause) {
    if (isDevEnv())
      console.warn('[i18n] Initial locale load failed; staying on the default.', cause)
  }
  app.use(i18n)
}
